/**
 * Halving Tracker - Era timeline, mint curve chart, next mint preview
 * 108-hotel halving cycle with 10 eras, 50% reduction each era
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, ChevronRight, Coins,
  Flame, Layers, RefreshCw, Sparkles, Timer, TrendingDown,
  Zap,
} from 'lucide-react';
import { getHalvingStatus, getHalvingSchedule } from '@/lib/v2Api';
import { BACKEND_URL } from '@/lib/backendUrl';

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

// ─── Hero Countdown ───
const HeroCountdown = ({ status }) => {
  const spotsLeft = status.hotels_until_next_halving;
  const urgency = spotsLeft <= 10 ? 'text-red-500' : spotsLeft <= 30 ? 'text-amber-500' : 'text-[hsl(var(--web3-primary))]';
  const progressPct = status.hotels_registered > 0
    ? ((status.hotels_registered % 108) / 108) * 100
    : 0;

  return (
    <Card className="relative overflow-hidden border-[hsl(var(--web3-primary))]/20" data-testid="hero-countdown">
      <div className="absolute inset-0 bg-[radial-gradient(60%_40%_at_80%_10%,rgba(14,165,164,0.06)_0%,transparent_60%)]" />
      <CardContent className="relative pt-6 pb-6 px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          {/* Big Number */}
          <div className="text-center" data-testid="countdown-number">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Hotels until next halving</p>
            <p className={`text-7xl md:text-8xl heading-font font-black tabular-nums leading-none ${urgency}`}>
              {spotsLeft}
            </p>
            <p className="text-sm text-muted-foreground mt-3">of 108 per era</p>
            {spotsLeft <= 20 && (
              <div className="mt-2 flex items-center gap-1.5 justify-center">
                <Flame className="h-3.5 w-3.5 text-amber-500 animate-pulse" />
                <span className="text-xs font-medium text-amber-500">Filling fast</span>
              </div>
            )}
          </div>

          {/* Era Info */}
          <div className="space-y-4 text-center" data-testid="era-info">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Current Era</p>
              <p className="text-4xl heading-font font-bold mt-1">Era {status.current_era}</p>
            </div>
            <div className="flex items-center justify-center gap-6">
              <div>
                <p className="text-2xl heading-font font-bold tabular-nums text-emerald-500">{fmt(status.current_mint_rate)}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Per Hotel</p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-2xl heading-font font-bold tabular-nums text-muted-foreground">{fmt(status.next_mint_rate)}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Next Era</p>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="space-y-3" data-testid="era-progress">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Era Progress</span>
              <span className="text-xs heading-font tabular-nums font-medium">{progressPct.toFixed(1)}%</span>
            </div>
            <Progress value={progressPct} className="h-3 [&>div]:bg-gradient-to-r [&>div]:from-[hsl(var(--web3-primary))] [&>div]:to-emerald-400" />
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="text-center p-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/60">
                <p className="text-lg heading-font font-bold tabular-nums">{fmt(status.hotels_registered)}</p>
                <p className="text-[10px] text-muted-foreground">Hotels Registered</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/60">
                <p className="text-lg heading-font font-bold tabular-nums">{fmt(status.total_minted)}</p>
                <p className="text-[10px] text-muted-foreground">Total Minted</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Mint Curve Chart (pure CSS bars) ───
const MintCurveChart = ({ eras, currentEra }) => {
  const maxMint = eras[0]?.mint_per_hotel || 100000;
  const totalSupply = eras.reduce((sum, e) => sum + (e.mint_per_hotel * 108), 0);

  return (
    <Card data-testid="mint-curve-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
          Mint Curve
        </CardTitle>
        <CardDescription>HERMES minted per hotel across 10 eras — 50% halving every 108 hotels</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2 h-[220px]" data-testid="mint-bars">
          {eras.map((era) => {
            const heightPct = (era.mint_per_hotel / maxMint) * 100;
            const isActive = era.era === currentEra;
            const isPast = era.era < currentEra;
            const eraMint = era.mint_per_hotel * 108;
            const pctOfTotal = ((eraMint / totalSupply) * 100).toFixed(1);
            return (
              <div key={era.era} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                {/* Tooltip */}
                <div className="opacity-0 group-hover:opacity-100 absolute -top-2 left-1/2 -translate-x-1/2 bg-popover border rounded-lg px-3 py-2 text-xs whitespace-nowrap z-10 pointer-events-none transition-opacity shadow-lg">
                  <p className="font-medium">Era {era.era}</p>
                  <p className="text-muted-foreground">Hotels {era.hotel_start}–{era.hotel_end}</p>
                  <p className="tabular-nums">{fmt(era.mint_per_hotel)} per hotel</p>
                  <p className="text-muted-foreground">{fmt(eraMint)} era total ({pctOfTotal}%)</p>
                </div>
                {/* Value label */}
                <p className="text-[9px] heading-font tabular-nums font-medium mb-1 text-muted-foreground">
                  {era.mint_per_hotel >= 1000 ? `${(era.mint_per_hotel / 1000).toFixed(era.mint_per_hotel >= 10000 ? 0 : 1)}k` : era.mint_per_hotel}
                </p>
                {/* Bar */}
                <div
                  className={`w-full rounded-t-md transition-all duration-500 ${
                    isActive
                      ? 'bg-gradient-to-t from-[hsl(var(--web3-primary))] to-emerald-400 shadow-md shadow-[hsl(var(--web3-primary))]/20'
                      : isPast
                      ? 'bg-emerald-500/40'
                      : 'bg-[hsl(var(--web3-surface-2))]/80'
                  }`}
                  style={{ height: `${Math.max(heightPct, 3)}%` }}
                />
                {/* Label */}
                <p className={`text-[10px] mt-2 tabular-nums ${
                  isActive ? 'font-bold text-[hsl(var(--web3-primary))]' : 'text-muted-foreground'
                }`}>
                  E{era.era}
                </p>
              </div>
            );
          })}
        </div>
        <Separator className="mt-4 mb-3" />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Total Registration Supply: {fmt(totalSupply)} HERMES</span>
          <span>Halving interval: 108 hotels</span>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Era Timeline ───
const EraTimeline = ({ eras, currentEra, hotelsRegistered }) => (
  <Card data-testid="era-timeline-card">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg heading-font flex items-center gap-2">
        <Layers className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
        Era Timeline
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ScrollArea className="h-[350px]">
        <div className="space-y-1">
          {eras.map((era) => {
            const isActive = era.era === currentEra;
            const isPast = era.era < currentEra;
            const hotelsInEra = isActive ? (hotelsRegistered % 108 || (isPast ? 108 : 0)) : (isPast ? 108 : 0);
            const eraMint = era.mint_per_hotel * 108;

            return (
              <div
                key={era.era}
                className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                  isActive ? 'bg-[hsl(var(--web3-primary))]/10 border border-[hsl(var(--web3-primary))]/20' :
                  isPast ? 'opacity-60' : ''
                }`}
                data-testid={`era-row-${era.era}`}
              >
                {/* Timeline dot */}
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <div className={`h-4 w-4 rounded-full border-2 ${
                    isActive ? 'bg-[hsl(var(--web3-primary))] border-[hsl(var(--web3-primary))]' :
                    isPast ? 'bg-emerald-500 border-emerald-500' :
                    'bg-transparent border-border'
                  }`} />
                </div>

                {/* Era Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium heading-font">Era {era.era}</span>
                    {isActive && <Badge className="text-[10px] bg-[hsl(var(--web3-primary))]/15 text-[hsl(var(--web3-primary))] border-[hsl(var(--web3-primary))]/30">Active</Badge>}
                    {isPast && <Badge variant="outline" className="text-[10px]">Completed</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">Hotels {era.hotel_start} — {era.hotel_end}</p>
                </div>

                {/* Mint Rate */}
                <div className="text-right shrink-0">
                  <p className="text-sm heading-font tabular-nums font-semibold">{fmt(era.mint_per_hotel)}</p>
                  <p className="text-[10px] text-muted-foreground">per hotel</p>
                </div>

                {/* Era Total */}
                <div className="text-right shrink-0 w-24">
                  <p className="text-xs heading-font tabular-nums text-muted-foreground">{fmt(eraMint)}</p>
                  <p className="text-[10px] text-muted-foreground">era total</p>
                </div>

                {/* Progress */}
                <div className="w-20 shrink-0">
                  <Progress
                    value={isActive ? (hotelsInEra / 108) * 100 : (isPast ? 100 : 0)}
                    className="h-1.5"
                  />
                  <p className="text-[10px] text-muted-foreground text-right mt-0.5 tabular-nums">
                    {isActive ? `${hotelsInEra}/108` : isPast ? '108/108' : '0/108'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </CardContent>
  </Card>
);

// ─── Next Mint Preview ───
const NextMintPreview = ({ preview }) => {
  if (!preview) return null;
  const dist = preview.distribution;
  const segments = [
    { label: 'Staked', value: dist.staked_on_protocol, color: 'bg-violet-500' },
    { label: 'Sage Treasury', value: dist.sage_treasury, color: 'bg-emerald-500' },
    { label: 'Bitsy Wallet', value: dist.bitsy_wallet, color: 'bg-amber-500' },
    { label: 'Protocol Pool', value: dist.protocol_pool, color: 'bg-blue-500' },
    { label: 'Sage Buffer', value: dist.sage_buffer, color: 'bg-teal-500' },
  ];

  return (
    <Card data-testid="next-mint-preview">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
          Next Mint Preview
        </CardTitle>
        <CardDescription>Hotel #{preview.hotel_number} (Era {preview.era})</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Total */}
        <div className="text-center py-2">
          <p className="text-3xl heading-font font-bold tabular-nums">{fmt(preview.mint_amount)}</p>
          <p className="text-xs text-muted-foreground">HERMES to mint</p>
          {preview.buy_in_required > 0 && (
            <Badge variant="outline" className="mt-2 text-xs text-amber-500 border-amber-500/30">
              Buy-in: {fmt(preview.buy_in_required)} HERMES
            </Badge>
          )}
        </div>

        {/* Distribution Bar */}
        <div className="flex h-4 rounded-full overflow-hidden" data-testid="distribution-bar">
          {segments.map((seg) => {
            const pct = (seg.value / dist.total_minted) * 100;
            return pct > 0 ? (
              <div key={seg.label} className={`${seg.color} relative group`} style={{ width: `${pct}%` }}>
                <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border rounded px-2 py-0.5 text-[10px] whitespace-nowrap z-10 pointer-events-none transition-opacity">
                  {seg.label}: {fmt(seg.value)} ({pct.toFixed(1)}%)
                </div>
              </div>
            ) : null;
          })}
        </div>

        {/* Legend */}
        <div className="space-y-2">
          {segments.map((seg) => (
            <div key={seg.label} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className={`h-2.5 w-2.5 rounded-sm ${seg.color}`} />
                <span className="text-muted-foreground">{seg.label}</span>
              </div>
              <span className="heading-font tabular-nums font-medium">{fmt(seg.value)}</span>
            </div>
          ))}
        </div>

        <Separator />

        {/* Supply Stats */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="p-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/60 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Free Buy-in</p>
            <p className="heading-font font-medium">{preview.buy_in_required === 0 ? 'Yes' : `${fmt(preview.buy_in_required)}`}</p>
          </div>
          <div className="p-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/60 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Referral Bonus</p>
            <p className="heading-font font-medium">{dist.referral_bonus > 0 ? fmt(dist.referral_bonus) : 'None'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Supply Summary ───
const SupplyCard = ({ status }) => {
  const remaining = status.total_remaining_registration_supply;
  const totalSupply = 21600000; // Approximate total registration supply
  const mintedPct = ((status.total_minted / totalSupply) * 100).toFixed(2);

  return (
    <Card data-testid="supply-summary-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Coins className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
          Supply Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Minted</span>
            <span className="heading-font tabular-nums font-medium">{fmt(status.total_minted)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Remaining Supply</span>
            <span className="heading-font tabular-nums font-medium">{fmt(remaining)}</span>
          </div>
          <Progress value={Number(mintedPct)} className="h-2" />
          <p className="text-[10px] text-muted-foreground text-right">{mintedPct}% of registration supply minted</p>
        </div>
        <Separator />
        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="p-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/60">
            <p className="text-lg heading-font font-bold">{status.hotels_registered}</p>
            <p className="text-[10px] text-muted-foreground">Hotels Onboarded</p>
          </div>
          <div className="p-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/60">
            <p className="text-lg heading-font font-bold">{1080 - status.hotels_registered}</p>
            <p className="text-[10px] text-muted-foreground">Spots Remaining</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main Page ───
export default function HalvingTrackerPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sc] = await Promise.all([
        getHalvingStatus(),
        getHalvingSchedule(),
      ]);
      setStatus(s);
      setSchedule(sc);
      // Preview is fetched from the halving status endpoint indirectly
      const previewRes = await fetch(`${BACKEND_URL}/api/blockchain/v2/halving/preview-mint`);
      setPreview(await previewRes.json());
    } catch { toast.error('Failed to load halving data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading || !status || !schedule) {
    return (
      <div className="admin-theme min-h-dvh bg-background text-foreground">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
          <Skeleton className="h-48 w-full" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <Skeleton className="h-96 lg:col-span-7" />
            <Skeleton className="h-96 lg:col-span-5" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-theme min-h-dvh bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl heading-font font-semibold tracking-tight" data-testid="page-title">Halving Tracker</h1>
              <p className="text-xs text-muted-foreground">108-Hotel Cycle | 10 Eras | 50% Reduction</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
        <HeroCountdown status={status} />

        <MintCurveChart eras={schedule.eras} currentEra={status.current_era} />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <EraTimeline eras={schedule.eras} currentEra={status.current_era} hotelsRegistered={status.hotels_registered} />
          </div>
          <div className="lg:col-span-5 space-y-5">
            <NextMintPreview preview={preview} />
            <SupplyCard status={status} />
          </div>
        </div>
      </div>
    </div>
  );
}
